/**
 * Modal de Solicitação de Unpicking (Fase 4, Conferência de Saída Web)
 *
 * Permite ao usuário solicitar o unpicking de uma linha específica de saída.
 * Campos: quantidade, motivo e observação.
 *
 * Regras:
 *  • Disponível para saídas em status 30/40/50/60 (antes da baixa/NF-e).
 *  • Não movimenta estoque — apenas registra a solicitação.
 *  • Bloqueia nova solicitação se já existe uma REQUESTED ou APPROVED para o mesmo item.
 *
 * Uso:
 *   import { openUnpickRequestModal } from './outputs-unpick.js';
 *   openUnpickRequestModal(companyId, outputId, item, onSuccess);
 */

import api from '../api.js';
import dialog from '../../versa/dialog.js';
import { notify } from '../../versa/msgbox.js';

function escapeHtml(value) {
	if (value == null) return '';
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

/** Mapeamento de códigos de motivo para exibição */
export const UNPICK_REASON_LABELS = {
	ERRO_PICKING:             'Erro de picking',
	LOTE_ERRADO:              'Lote errado',
	ITEM_ERRADO:              'Item errado',
	QUANTIDADE_ERRADA:        'Quantidade errada',
	AVARIA:                   'Avaria',
	CANCELAMENTO_OPERACIONAL: 'Cancelamento operacional',
	OUTROS:                   'Outros',
};

/** Mapeamento de status de solicitação para exibição */
export const UNPICK_STATUS_LABELS = {
	REQUESTED: { label: 'Solicitado',  badge: 'warning' },
	APPROVED:  { label: 'Aprovado',    badge: 'success' },
	REJECTED:  { label: 'Rejeitado',   badge: 'danger'  },
	CANCELED:  { label: 'Cancelado',   badge: 'secondary' },
	EXECUTED:  { label: 'Executado',   badge: 'info'    },
};

/**
 * Retorna o badge HTML para o status de uma solicitação de unpicking.
 * @param {string} status
 */
export function unpickStatusBadge(status) {
	const info = UNPICK_STATUS_LABELS[status];
	if (!info) return '';
	return `<span class="badge badge-${info.badge}" title="Unpicking ${info.label}">${info.label}</span>`;
}

/**
 * Abre o modal para solicitar unpicking de uma linha de saída.
 *
 * @param {string} companyId
 * @param {number} outputId
 * @param {{ number: number, product_name?: string, product_sku?: string, quantity_required: number, quantity_supplied?: number, unpick_status?: string }} item
 * @param {string} outputStatus - status da saída (usado para calcular max_qty)
 * @param {Function} [onSuccess] - callback(requestId) chamado após criação com sucesso
 */
export function openUnpickRequestModal(companyId, outputId, item, outputStatus, onSuccess) {
	const itemNumber   = item.number;
	const productLabel = item.product_name
		? `${item.product_sku || ''} — ${item.product_name}`.trim().replace(/^—\s*/, '')
		: `Item ${itemNumber}`;

	// Limite de quantidade: pós-conferência (60) usa quantity_supplied; demais usam quantity_required
	const maxQty = parseInt(
		outputStatus === 60 || outputStatus === '60'
			? (item.quantity_supplied ?? item.quantity_required ?? 1)
			: (item.quantity_required ?? 1),
		10
	);

	const reasonOptions = Object.entries(UNPICK_REASON_LABELS)
		.map(([code, label]) => `<option value="${code}">${label}</option>`)
		.join('');

	const html = `
		<div class="msgbox" style="max-width:560px;min-width:360px">
		<div class="mb-title">Solicitar Unpicking — Linha ${escapeHtml(itemNumber)}</div>
		<div class="mb-text">
		<div class="form-group">
			<div class="form-row mb-2">
				<label class="col-form-label col-12 text-muted small">Item</label>
				<div class="col-12"><strong>${escapeHtml(productLabel)}</strong> (linha ${escapeHtml(itemNumber)})</div>
			</div>
			<div class="form-row mb-2">
				<div class="form-group col-12">
					<label class="col-form-label">Quantidade a desabonar
						<span class="text-muted small">(máx. ${escapeHtml(maxQty)})</span>
					</label>
					<input id="unpick-qty" type="number" class="form-control"
						min="1" max="${escapeHtml(maxQty)}" value="${escapeHtml(maxQty)}" />
				</div>
			</div>
			<div class="form-row mb-2">
				<div class="form-group col-12">
					<label class="col-form-label">Motivo <span class="text-danger">*</span></label>
					<select id="unpick-reason" class="form-control">
						<option value="">— selecione —</option>
						${reasonOptions}
					</select>
				</div>
			</div>
			<div class="form-row mb-2" id="unpick-note-row">
				<div class="form-group col-12">
					<label class="col-form-label">Observação
						<span id="unpick-note-required" class="text-danger d-none">*</span>
					</label>
					<textarea id="unpick-note" class="form-control" rows="2"
						placeholder="Descreva o motivo da solicitação"></textarea>
				</div>
			</div>
		</div>
		</div>
		<div class="modal-footer px-0">
			<button type="button" class="button mtl bn_N" id="unpick-cancel-btn">Cancelar</button>
			<button type="button" class="button mtl bn_Y" id="unpick-submit-btn">Solicitar Unpicking</button>
		</div>
		</div>
	`;

	const dlg = dialog.create(html, async (key) => {
		if (key === 'Escape' || key === 'N') {
			dlg.destroy();
			return;
		}
		if (key !== 'Y') return;

		const node = dlg.getNode();
		const reasonSel = node.querySelector('#unpick-reason');
		const submitBtn = node.querySelector('#unpick-submit-btn');
		const qtyInput  = node.querySelector('#unpick-qty');
		const noteInput = node.querySelector('#unpick-note');
		const qty        = parseInt(qtyInput.value, 10);
		const reasonCode = reasonSel.value;
		const reasonNote = noteInput.value.trim();

		if (!qty || qty <= 0) {
			notify('Informe uma quantidade maior que zero.', 'warning');
			return;
		}
		if (qty > maxQty) {
			notify(`Quantidade máxima permitida: ${maxQty}.`, 'warning');
			return;
		}
		if (!reasonCode) {
			notify('Selecione um motivo.', 'warning');
			return;
		}
		if (reasonCode === 'OUTROS' && !reasonNote) {
			notify('Observação é obrigatória quando o motivo for "Outros".', 'warning');
			return;
		}

		submitBtn.disabled = true;
		submitBtn.textContent = 'Aguarde…';

			const result = await api.call(
				api.request_output_unpick,
				companyId,
				outputId,
				{
					output_item_number: itemNumber,
					quantity:           qty,
					reason_code:        reasonCode,
					reason_note:        reasonNote || undefined,
				}
			);

		if (!result) {
			submitBtn.disabled = false;
			submitBtn.textContent = 'Solicitar Unpicking';
			return;
		}

		dlg.destroy();
		notify('Solicitação de unpicking registrada com sucesso.', 'success');

		if (typeof onSuccess === 'function') {
			onSuccess(result.id);
		}
	}, /Escape|N|Y/);

	const node = dlg.getNode();
	const reasonSel = node.querySelector('#unpick-reason');
	const noteReq = node.querySelector('#unpick-note-required');
	const noteInput = node.querySelector('#unpick-note');

	// Exibe asterisco no campo observação quando motivo é OUTROS.
	reasonSel.addEventListener('change', () => {
		if (reasonSel.value === 'OUTROS') {
			noteReq.classList.remove('d-none');
			noteInput.placeholder = 'Obrigatório para o motivo "Outros"';
		} else {
			noteReq.classList.add('d-none');
			noteInput.placeholder = 'Descreva o motivo da solicitação';
		}
	});
}
